import { Neighborhood, PlayerState, GameState } from '../domain/types.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';

export const FAKE_NEIGHBORHOOD: Neighborhood = {
  id: 'fake-pho-hue',
  label: 'Phố Huế & Bà Triệu (Demo Seed)',
  center: {
    lat: 21.0135,
    lon: 105.8504,
  },
  radiusM: 500,
  dna: {
    density: 0.85,
    roadTraffic: 0.9,
    pedestrianFlow: 0.7,
    schoolFlow: 0.6,
    hospitalFlow: 0.4,
    marketFlow: 0.8,
    nightFlow: 0.75,
    competition: 0.65,
    risk: 0.35,
  },
  roads: [
    {
      id: 'road-pho-hue',
      name: 'Phố Huế',
      roadClass: 'primary',
      points: [
        { x: -120, y: -250 },
        { x: -100, y: 0 },
        { x: -80, y: 250 },
      ],
    },
    {
      id: 'road-ba-trieu',
      name: 'Bà Triệu',
      roadClass: 'primary',
      points: [
        { x: 80, y: -250 },
        { x: 100, y: 0 },
        { x: 120, y: 250 },
      ],
    },
    {
      id: 'road-ngo-ngang',
      name: 'Ngõ Phố Huế',
      roadClass: 'residential',
      points: [
        { x: -100, y: 0 },
        { x: 0, y: 10 },
        { x: 100, y: 0 },
      ],
    },
  ],
  pois: [
    {
      id: 'poi-cho-hom',
      name: 'Chợ Hôm - Đức Viên',
      type: 'market',
      position: { x: -90, y: -80 },
    },
    {
      id: 'poi-thcs-doan-ket',
      name: 'THCS Đoàn Kết',
      type: 'school',
      position: { x: 90, y: 120 },
    },
    {
      id: 'poi-bus-pho-hue',
      name: 'Điểm xe buýt Phố Huế',
      type: 'bus_stop',
      position: { x: -110, y: 40 },
    },
  ],
  nodes: [
    {
      id: 'node-cho-hom-gate',
      x: -95,
      y: -75,
      sourcePoiId: 'poi-cho-hom',
      roadType: 'primary',
      trafficBase: 0.9,
      visibility: 0.95,
      density: 0.85,
      risk: 0.4,
      unlockCost: 300_000,
      rentPerDay: 50_000,
      tags: ['market_entrance', 'high_morning_traffic'],
    },
    {
      id: 'node-ngo-pho-hue',
      x: -5,
      y: 8,
      roadType: 'residential',
      trafficBase: 0.6,
      visibility: 0.5,
      density: 0.7,
      risk: 0.15,
      unlockCost: 150_000,
      rentPerDay: 20_000,
      tags: ['alley', 'low_risk'],
    },
    {
      id: 'node-gate-school',
      x: 95,
      y: 110,
      sourcePoiId: 'poi-thcs-doan-ket',
      roadType: 'primary',
      trafficBase: 0.85,
      visibility: 0.8,
      density: 0.8,
      risk: 0.35,
      unlockCost: 250_000,
      rentPerDay: 40_000,
      tags: ['school_gate', 'student_peak'],
    },
    {
      id: 'node-bus-stop',
      x: -115,
      y: 45,
      sourcePoiId: 'poi-bus-pho-hue',
      roadType: 'primary',
      trafficBase: 0.8,
      visibility: 0.85,
      density: 0.75,
      risk: 0.3,
      unlockCost: 200_000,
      rentPerDay: 35_000,
      tags: ['bus_stop', 'pulse_flow'],
    },
    {
      id: 'node-ba-trieu-junction',
      x: 105,
      y: -10,
      roadType: 'primary',
      trafficBase: 0.88,
      visibility: 0.9,
      density: 0.8,
      risk: 0.45,
      unlockCost: 400_000,
      rentPerDay: 60_000,
      tags: ['crossroad', 'prime_location'],
    },
  ],
};

export const INITIAL_PLAYER_STATE: PlayerState = {
  id: 'player-local',
  username: '',
  stallName: '',
  money: GAME_CONFIG.economy.startingMoney,
  reputation: 50,
  heat: 0.05,
  rank: GAME_CONFIG.progression.ranks[0],
  prestige: 0,
  neighborhoodId: FAKE_NEIGHBORHOOD.id,
  ownedStalls: [],
  activeMenu: ['tra_da', 'nhan_tran'],
  upgrades: {},
  totalRevenue: 0,
  totalProfit: 0,
  totalSales: 0,
  secretKey: 'sec_localplayer01',
  stallLevel: 1,
  unlockedPerks: [],
  hasStaff: false,
};

export const createInitialGameState = (seed = 42): GameState => {
  const now = Date.now();
  return {
    phase: 'BOOT',
    tick: 0,
    lastTickAt: now,
    player: { ...INITIAL_PLAYER_STATE },
    neighborhood: FAKE_NEIGHBORHOOD,
    stalls: {},
    ghostStalls: [],
    dummyRivals: {},
    activeEvent: null,
    inspectNodeId: null,
    inspectRivalId: null,
    narrativeQuote: '👵 Bà Trà Đá: "Vỉa hè góc phố này đắc địa lắm sếp ơi! Chọn một chỗ mát mẻ rồi kê bàn dọn hàng kiếm tiền nào!"',
    currentTrendingTopic: 'Đội tuyển Việt Nam thắng kịch tính',
    seed,
    recentSales: [],
    latestSale: null,
    loadingMessage: null,
    dayNumber: 1,
    dayStats: {
      dayNumber: 1,
      startedAt: now,
      revenue: 0,
      cost: 0,
      profit: 0,
      salesCount: 0,
      rentPaid: 0,
    },
    dayHistory: [],
    showDayReport: false,
    weather: {
      type: 'sunny',
      name: 'Nắng Đẹp',
      emoji: '☀️',
      trafficMultiplier: 1.0,
      description: 'Hôm nay nắng, khách đông!',
    },
    pendingMilestone: null,
    pendingCelebration: null,
    currentShift: 'morning',
    isWeekend: false,
    isOvertimeActive: false,
    dayOfWeek: 'Thứ Hai',
    isPaused: false,
  };
};
